import { afterNextRender, Component, ElementRef, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { EmployeeComponentState } from '../../shared/state-interface';
import { CountriesComponent } from '../../ui-components/countries/countries.component';
import { Employee } from './employee.model';

@Component({
    selector: 'app-employee-component',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        CountriesComponent
    ],
    templateUrl: './employee.component.html',
    styleUrl: './employee.component.css'
})
export class EmployeeComponent {

    @ViewChild(CountriesComponent) countriesComponent!:CountriesComponent;
    @Output() messageEvent = new EventEmitter<string>();

    private elementRef: ElementRef = inject(ElementRef);

    protected employeeForm = new FormGroup({
        firstName: new FormControl(""),
        lastName: new FormControl(""),
        email: new FormControl(""),
        phone: new FormControl(""),
        addressLine1: new FormControl(""),
        addressLine2: new FormControl(""),
        addressLine3: new FormControl("")
    })

    public constructor() {
        afterNextRender(() => {
            // This LifeCycle hook primarily supports the "Add" Use Case and
            // the browser back button Use Case. The setTimeout is necessary
            // to put these commands at the end of the event queue in attempt
            // to delay their invocation until the DOM is fully updated. Same
            // functions are invoked from the populateFormFromModel function
            // to support the "Update" Use Case.
            setTimeout(() => {
                const ref = this.elementRef.nativeElement.querySelector("input");
                ref?.focus();
                ref?.select();
            }, 0);
        });
    }

    public getState(): EmployeeComponentState {
        const state: EmployeeComponentState = {} as EmployeeComponentState
        state.employeeForm = this.employeeForm!;
        state.countriesComponentState = this.countriesComponent.getState();
        return state;
    }

    public restoreState(state: EmployeeComponentState) {
        this.employeeForm = state.employeeForm;
        this.countriesComponent.restoreState(state.countriesComponentState);
    }

    public init(countryCode?: string) {
        this.countriesComponent.countryCode = countryCode;
        this.countriesComponent.init();
    }

    public receiveMessage(value: string) {
        this.messageEvent.emit(value);
    }

    public populateFormFromModel(employee: Employee) {
        this.firstNameCtl.setValue(employee.first_name);
        this.lastNameCtl.setValue(employee.last_name);
        this.emailCtl.setValue(employee.email);
        this.phoneCtl.setValue(employee.phone);
        this.addressLine1Ctl.setValue(employee.address_line_1);
        this.addressLine2Ctl.setValue(employee.address_line_2);
        this.addressLine3Ctl.setValue(employee.address_line_3 ? employee.address_line_3 : "");
        this.countriesComponent.countryCode = employee.country_code;

        const ref = this.elementRef.nativeElement.querySelector("input");
        ref?.focus();
        ref?.select();
    }

    public createModelFromForm(employee: Employee): Employee {
        employee.first_name = this.firstNameCtl.value;
        employee.last_name = this.lastNameCtl.value;
        employee.email = this.emailCtl.value;
        employee.phone = this.phoneCtl.value;
        employee.address_line_1 = this.addressLine1Ctl.value;
        employee.address_line_2 = this.addressLine2Ctl.value;
        employee.address_line_3 = this.addressLine3Ctl.value;
        employee.country_code = this.countriesComponent.selectedCountry;
        return employee;
    }

    private get lastNameCtl(): FormControl {
        return this.employeeForm.controls.lastName;
    }

    private get firstNameCtl(): FormControl {
        return this.employeeForm.controls.firstName;
    }

    private get emailCtl(): FormControl {
        return this.employeeForm.controls.email;
    }

    private get phoneCtl(): FormControl {
        return this.employeeForm.controls.phone;
    }

    private get addressLine1Ctl(): FormControl {
        return this.employeeForm.controls.addressLine1;
    }

    private get addressLine2Ctl(): FormControl {
        return this.employeeForm.controls.addressLine2;
    }

    private get addressLine3Ctl(): FormControl {
        return this.employeeForm.controls.addressLine3;
    }
}